import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useClaudeConnectorEnabled } from "../../hooks/useClaudeConnectorEnabled";
import { ClaudeConnectorLink, ClaudeLogo } from "./ClaudeConnectorLink";
import { CLAUDE_CONNECTOR_TOPICS, ClaudeConnectorContext, ClaudeProductKind } from "./claudeConnectorConfig";

interface Props {
  context: ClaudeConnectorContext | null;
  productKind?: ClaudeProductKind;
}

export function ClaudeConnectorHeader({ context, productKind = "portfolio" }: Props) {
  const enabled = useClaudeConnectorEnabled({ allowSignedOut: context === "share-in" || context === "share-out" });
  const { user } = useAuth();
  const { pathname } = useLocation();
  return enabled && context ? (
    <ConnectorPopover key={`${user?.id}:${pathname}:${context}:${productKind}`} context={context} productKind={productKind} />
  ) : null;
}

function ConnectorPopover({ context, productKind }: { context: ClaudeConnectorContext; productKind: ClaudeProductKind }) {
  const id = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(false);
  const restoringFocusRef = useRef(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 16 });
  const isShare = context === "share-in" || context === "share-out";
  const topic = isShare && productKind === "basket"
    ? CLAUDE_CONNECTOR_TOPICS[context].replace("portfolio", "basket")
    : CLAUDE_CONNECTOR_TOPICS[context];

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const close = useCallback((restoreFocus = false) => {
    cancelClose();
    pinnedRef.current = false;
    setOpen(false);
    if (restoreFocus) {
      restoringFocusRef.current = true;
      triggerRef.current?.focus();
      restoringFocusRef.current = false;
    }
  }, [cancelClose]);

  const enter = () => {
    cancelClose();
    if (!restoringFocusRef.current) setOpen(true);
  };

  const leave = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      if (!pinnedRef.current && !panelRef.current?.contains(document.activeElement)
        && document.activeElement !== triggerRef.current) close();
    }, 180);
  };

  const blur = (event: React.FocusEvent) => {
    const next = event.relatedTarget as Node | null;
    if (!triggerRef.current?.contains(next) && !panelRef.current?.contains(next)) close();
  };

  useEffect(() => cancelClose, [cancelClose]);

  useLayoutEffect(() => {
    if (!open) return;
    const reposition = () => {
      const trigger = triggerRef.current?.getBoundingClientRect();
      if (!trigger) return;
      const viewportWidth = document.documentElement.clientWidth;
      const width = Math.min(480, viewportWidth - 32);
      const height = panelRef.current?.getBoundingClientRect().height ?? 0;
      setPosition({
        left: Math.max(16, Math.min(trigger.right - width, viewportWidth - width - 16)),
        top: Math.max(16, Math.min(trigger.bottom + 10, window.innerHeight - height - 16)),
      });
    };
    reposition();
    window.addEventListener("resize", reposition);
    document.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      document.removeEventListener("scroll", reposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !panelRef.current?.contains(target)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close(true);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Plutus works with Claude"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onMouseEnter={enter}
        onMouseLeave={leave}
        onFocus={enter}
        onBlur={blur}
        onClick={() => {
          if (pinnedRef.current) close(true);
          else {
            cancelClose();
            pinnedRef.current = true;
            setOpen(true);
          }
        }}
        onKeyDown={(event) => {
          if (open && event.key === "Tab" && !event.shiftKey) {
            event.preventDefault();
            panelRef.current?.querySelector<HTMLAnchorElement>("a")?.focus();
          }
        }}
        className={`inline-flex shrink-0 items-center justify-center rounded-v7-control border border-v7-ink/20 bg-v7-white transition-colors hover:bg-v7-paper focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark ${isShare ? "h-8 w-8" : "h-11 w-11"}`}
      >
        <ClaudeLogo className={isShare ? "h-4 w-4" : "h-5 w-5"} />
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          id={id}
          role="dialog"
          aria-labelledby={`${id}-title`}
          aria-describedby={`${id}-description`}
          onMouseEnter={cancelClose}
          onMouseLeave={leave}
          onBlur={blur}
          onKeyDown={(event) => {
            if (event.key !== "Tab") return;
            const controls = panelRef.current?.querySelectorAll<HTMLElement>("a, button");
            if (!controls?.length) return;
            if (event.shiftKey && event.target === controls[0]) {
              event.preventDefault();
              triggerRef.current?.focus();
            } else if (!event.shiftKey && event.target === controls[controls.length - 1]) {
              event.preventDefault();
              // The portal is at the end of body; resume after its trigger instead.
              const pageControls = Array.from(document.querySelectorAll<HTMLElement>(
                'a[href], button, input, select, textarea, [tabindex]'
              )).filter((element) => element.tabIndex >= 0
                && !element.matches(':disabled')
                && !element.closest('[hidden], [inert]')
                && !panelRef.current?.contains(element)
                && (element.checkVisibility?.({ checkVisibilityCSS: true }) ?? true));
              const next = pageControls[pageControls.indexOf(triggerRef.current!) + 1];
              close();
              if (next) next.focus();
              else close(true);
            }
          }}
          style={position}
          className="fixed z-[60] flex max-h-[calc(100dvh-2rem)] w-[480px] max-w-[calc(100%-2rem)] items-start gap-3 overflow-y-auto rounded-v7-control border border-v7-ink/15 bg-v7-white p-[18px] font-v7-sans text-v7-ink shadow-xl"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-v7-control bg-v7-paper">
            <ClaudeLogo />
          </div>
          <div className="min-w-0 flex-1 pr-3">
            <h2 id={`${id}-title`} className="mb-1 text-sm font-bold leading-snug">New: Plutus works with Claude</h2>
            <p id={`${id}-description`} className="text-xs leading-relaxed text-v7-ink-soft">
              Connect once and ask Claude about {topic}. Read-only; it can never trade or move money.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <ClaudeConnectorLink context={context} placement={isShare ? "share-header" : "header"} compact showLogo={false} />
              <button type="button" onClick={() => close(true)} className="min-h-8 rounded-v7-control border border-v7-ink/20 bg-v7-white px-3 py-1.5 text-[11px] font-medium hover:bg-v7-paper focus-visible:outline focus-visible:outline-2 focus-visible:outline-v7-bronze-dark">Not now</button>
            </div>
          </div>
          <button type="button" aria-label="Dismiss Claude card" onClick={() => close(true)} className="absolute right-2.5 top-2.5 flex h-7 w-7 items-center justify-center rounded-v7-control text-v7-ink/60 hover:bg-v7-paper hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-v7-bronze-dark">
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>, document.body,
      )}
    </>
  );
}
